import React from "react";
import { partnerLogos } from "../data/mockData";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { ExternalLink, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const PartnersSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const navigate = useNavigate();

  return (
    <section id="partners" className="py-28 bg-[#f5f5f7]">
      <div className="max-w-[1200px] mx-auto px-6">
        {/* PartnerLIFT Feature */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-20"
        >
          <p className="text-sm font-medium text-[#4a7c59] tracking-widest uppercase mb-3">
            Netzwerk
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-[#1d1d1f] tracking-tight mb-5">
            PartnerLIFT Netzwerk.
          </h2>
          <p className="text-lg text-[#6e6e73] max-w-2xl mx-auto">
            Über 15.000 Arbeitsbühnen im Netzwerk – bundesweit für Sie verfügbar,
            rund um die Uhr.
          </p>
        </motion.div>

        {/* PartnerLIFT Content */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mb-24"
        >
          <div className="bg-white rounded-3xl p-10 border border-[#e5e5e5]">
            <div className="flex items-center gap-3 mb-6">
              <img
                src="/uploads/partnerlift.png"
                alt="PartnerLIFT"
                className="h-10 object-contain"
              />
            </div>
            <p className="text-[#6e6e73] leading-relaxed mb-6 text-sm">
              Der PartnerLIFT-Mietgerätepool umfasst über 15.000 Arbeitsbühnen,
              Baumaschinen, Krane und Stapler, die Ihnen rund um die Uhr zur
              Verfügung stehen. Wir als PartnerLIFT-Betrieb betreuen Sie
              bundesweit mit Maschinen zu Bedingungen wie vor der Haustür!
            </p>
            <p className="text-[#6e6e73] leading-relaxed mb-8 text-sm">
              Durch das flächendeckende PartnerLIFT-Netzwerk sind wir in der
              Lage, Geräte überall in Deutschland und in einigen Nachbarländern
              bereitzustellen.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => navigate("/partnerlift")}
                className="inline-flex items-center gap-2 text-[#4a7c59] text-sm font-medium hover:underline"
              >
                Alle Partner anzeigen
                <ChevronRight size={14} />
              </button>
              <a
                href="https://www.partnerlift.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#4a7c59] text-sm font-medium hover:underline"
              >
                PartnerLIFT besuchen
                <ChevronRight size={14} />
              </a>
            </div>
          </div>

          <div className="relative">
            <div className="bg-white rounded-3xl p-10 border border-[#e5e5e5]">
              <h3 className="text-xl font-semibold text-[#1d1d1f] mb-4 tracking-tight">
                Qualitätssicherung
              </h3>
              <p className="text-[#6e6e73] leading-relaxed text-sm mb-6">
                PartnerLIFT Betriebe verpflichten sich zu regelmäßigen
                Überprüfungen der Service-Qualität. Von der Auftragsbearbeitung
                über die Wartung der Mietflotte bis hin zu Brandschutzroutinen
                werden alle erdenklichen Aspekte von professionellen Auditoren
                geprüft.
              </p>
              <a
                href="/uploads/ojm1an3u_Zertifikat-Brinckmann-2025.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 group cursor-pointer"
              >
                <img
                  src="/cert-thumbnail.png"
                  alt="Zertifikat"
                  className="w-12 h-14 rounded-md object-cover border border-[#e5e5e5] group-hover:border-[#4a7c59] transition-colors shadow-sm"
                />
                <div>
                  <p className="text-sm font-semibold text-[#1d1d1f] group-hover:text-[#4a7c59] transition-colors">
                    Zertifizierte Qualität
                  </p>
                  <p className="text-xs text-[#6e6e73]">
                    Zertifikat anzeigen (PDF)
                  </p>
                </div>
              </a>
            </div>
          </div>
        </motion.div>

        {/* Partner Logos */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.4 }}
        >
          <p className="text-center text-sm text-[#6e6e73] mb-8">
            Unsere geschätzten Partner
          </p>
          <div className="flex flex-wrap justify-center items-center gap-12">
            {partnerLogos.map((partner, i) => (
              <div
                key={i}
                className="grayscale hover:grayscale-0 opacity-50 hover:opacity-100 transition-all duration-500"
              >
                <img
                  src={partner.url}
                  alt={partner.name}
                  className="h-12 object-contain"
                />
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PartnersSection;
